import { ref, computed, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { calculateTax, createAccount, payment, getPaymentResult } from "@/api/discount-membership";
import { ElMessage } from "element-plus";
import { ACCOUNT_TYPE } from "@/const";
import { useProductStore } from "@/store/product";


export default function useNorthlandInsurance() {
    const router = useRouter();
    const step = ref(0); // for mobile view
    const productStore = useProductStore();
    // reactive data
    const accountType = ref(ACCOUNT_TYPE.DIAMOND_ANNUAL);
    const buttonLoading = ref(false);
    const pageLoading = ref(false);
    const showSuccessfulModal = ref(false);
    const showSubscribedModal = ref(false);
    const hubspotFormLoading = ref(false);
    const taxLoading = ref(false);
    const isTaxSuccess = ref(false);
    const taxAmount = ref(null);
    const hasRequestedTax = ref(false);
    const stripeCheckout = ref(null);
    const dataToken = ref({}); // Token returned after creating an account
    // unified form data object
    const formData = ref({

        // Step 2 - 账户信息
        firstName: "",
        lastName: "",
        email: "",
        phoneCountryCode: "+1",
        phone: "",

        // Step 3 - 账单地址
        streetAddress: "",
        city: "",
        zipCode: "",
        country: "",
        state: "",
    });

    // computed property: check if all forms are completed
    const isFormValid = computed(() => {
        const isStep2Valid =
            !!formData.value.firstName?.trim() &&
            !!formData.value.lastName?.trim() &&
            !!formData.value.email &&
            !!formData.value.phone

        const isStep3Valid =
            !!formData.value.streetAddress?.trim() &&
            !!formData.value.city?.trim() &&
            !!formData.value.zipCode?.trim() &&
            !!formData.value.country &&
            !!formData.value.state;

        return isStep2Valid && isStep3Valid;
    });
    // Step 4 form validation
    const isStep4Valid = computed(() => {
        return formData.value.checked1 === true &&
            formData.value.checked2 === true;
    });

    // computed property: get submit data
    const submitData = computed(() => {
        return {
            ...formData.value,
            dot: productStore.dot,
            phoneCountryCode: String(formData.value.phoneCountryCode).replace(/^\+/, ''),
            phone: String(formData.value.phone).replace(/\D/g, ''),
            productPlatform: "NORTHLAND_INSURANCE",
            productType: accountType.value,
        };
    });



    const onAccountTypeChange = (val) => {
        accountType.value = val;
        formData.value.accountType = val;
    };

    const onStep2FormChange = (stepFormData) => {
        // update the Step 2 fields in the unified form data
        Object.assign(formData.value, {
            firstName: stepFormData.firstName,
            lastName: stepFormData.lastName,
            email: stepFormData.email,
            phone: stepFormData.phone,
        });
        hasRequestedTax.value = false;
    };

    const onStep3FormChange = (stepFormData) => {
        // update the Step 3 fields in the unified form data
        Object.assign(formData.value, {
            streetAddress: stepFormData.streetAddress,
            city: stepFormData.city,
            zipCode: stepFormData.zipCode,
            country: stepFormData.country,
            state: stepFormData.state,
        });
        hasRequestedTax.value = false;
    };

    const onStep4FormChange = (stepFormData) => {
        // update the Step 4 fields in the unified form data
        Object.assign(formData.value, {
            checked1: stepFormData.checked1,
            checked2: stepFormData.checked2,
        });

    };


    const getTaxEstimate = async () => {
        taxLoading.value = true;
        try {
            // construct the request body, according to the interface documentation
            const body = {
                ...submitData.value,
            };
            delete body.checked1;
            delete body.checked2;
            const res = await calculateTax(body);
            if (res.code === 200 && res.data?.monetary_amount) {
                isTaxSuccess.value = true;
                taxAmount.value = res.data.monetary_amount.amount;
                // here we can assign the tax to the formData
                formData.value.tax = taxAmount.value;
            } else {
                ElMessage.error(res.message || "Tax calculation failed");
                isTaxSuccess.value = false;
            }
        } catch (err) {
            ElMessage.error(err.message || "Tax calculation failed");
            isTaxSuccess.value = false;
            throw err;
        } finally {
            taxLoading.value = false;
        }
    };
    const handleFormBlur = () => {
        if (isFormValid.value && !hasRequestedTax.value) {
            hasRequestedTax.value = true;
            getTaxEstimate();
        }
    }


    // Stripe payment popup
    const openStripe = () => {
        // add retry mechanism
        let retryCount = 0;
        const maxRetries = 3;

        const tryOpenStripe = () => {
            if (!window.StripeCheckout) {
                if (retryCount < maxRetries) {
                    retryCount++;
                    console.warn(`Stripe not loaded, retrying... (${retryCount}/${maxRetries})`);
                    setTimeout(tryOpenStripe, 1000);
                    return;
                } else {
                    ElMessage.error('Stripe failed to load, please refresh the page and try again!');
                    buttonLoading.value = false;
                    return;
                }
            }

            try {
                stripeCheckout.value = window.StripeCheckout.configure({
                    key: process.env.VUE_APP_STRIPE_KEY,
                    image: require("@/assets/svgs/logo.svg"),
                    locale: 'auto',
                    token(token) {
                        handleStripeToken(token.id);
                    }
                });

                stripeCheckout.value.open({
                    name: 'Card Information',
                    panelLabel: 'Pay Now',
                    email: formData.value.email,
                    closed() {
                        buttonLoading.value = false;
                    }
                });
            } catch (error) {
                console.error('Stripe configuration error:', error);
                ElMessage.error('Payment system initialization failed');
                buttonLoading.value = false;
            }
        };

        tryOpenStripe();
    };

    // Stripe payment callback
    const handleStripeToken = async (token) => {
        try {
            // 1. call the payment interface
            pageLoading.value = true;
            const payRes = await payment({
                token: token,
                parameter: dataToken.value,
            });
            if (payRes && payRes.code === 200) {
                stripeCheckout.value.confirm
                // 2. query the payment result
                const orderId = payRes?.data?.orderId;
                const result = await getPaymentResult({ orderId: orderId });
                if (result && result.code === 200) {
                    pageLoading.value = false;
                    router.push({ path: "/successful" });
                } else {
                    pageLoading.value = false;
                    ElMessage.error(result.message || 'Payment result query failed');

                }
            } else {
                pageLoading.value = false;
                ElMessage.error(payRes.message || 'Payment failed');

            }
        } catch (e) {
            ElMessage.error(e.message || 'Payment exception');
        } finally {
            pageLoading.value = false;
            buttonLoading.value = false;
        }
    };

    // submit order
    const submit = async () => {
        if (!isFormValid.value || !isStep4Valid.value || !hasRequestedTax.value) return;
        buttonLoading.value = true;
        try {
            const dataToSubmit = {
                ...submitData.value,
            };
            delete dataToSubmit.checked1;
            delete dataToSubmit.checked2;
            // 1. create account
            const accountRes = await createAccount(dataToSubmit);
            if (accountRes.code === 600) {
                showSubscribedModal.value = true;
                buttonLoading.value = false;
                return;
            }
            if (accountRes.code !== 200 || !accountRes?.data) {
                ElMessage.error(accountRes.message || 'Create account failed');
                buttonLoading.value = false;
                return;
            }



            dataToken.value = accountRes.data;
            // 2. open the Stripe payment popup
            openStripe();
        } catch (error) {
            ElMessage.error(error.message || "Submit failed");
            buttonLoading.value = false;
        }
    };



    const renderHubspotForm = () => {
        hubspotFormLoading.value = true;

        const checkHubspot = () => {
            if (window.hbspt && window.hbspt.forms) {
                window.hbspt.forms.create({
                    region: "na1",
                    portalId: "6474818",
                    formId: "21abef71-4606-4db5-8f72-ae53f712bf11",
                    target: "#hubspot-form-container",
                });

                // use a timer to check if the form is rendered
                const checkFormRendered = () => {
                    const container = document.getElementById("hubspot-form-container");
                    if (container && container.children.length > 0) {
                        // check if there is actual form content (not just loading)
                        const hasFormContent = container.querySelector('form') ||
                            container.querySelector('iframe') ||
                            container.querySelector('.hs-form');

                        if (hasFormContent) {
                            hubspotFormLoading.value = false;
                            return;
                        }
                    }

                    // if the form is not rendered yet, continue checking
                    setTimeout(checkFormRendered, 200);
                };

                // start checking if the form is rendered
                setTimeout(checkFormRendered, 500);

            } else {
                setTimeout(checkHubspot, 100);
            }
        };
        checkHubspot();
    };

    const handleContinue = (value) => {
        step.value = value;
    }

    const handleBack = (value) => {
        step.value = value;
    }

    watch(accountType, async (val) => {
        await nextTick();
        if (val === ACCOUNT_TYPE.FLEET_MONTH) {
            renderHubspotForm();
        } else {
            const container = document.getElementById("hubspot-form-container");
            if (container) container.innerHTML = "";
            hubspotFormLoading.value = false;
        }
    });
    return {
        // reactive data
        accountType,
        buttonLoading,
        pageLoading,
        showSuccessfulModal,
        showSubscribedModal,
        hubspotFormLoading,
        taxLoading,
        isTaxSuccess,
        taxAmount,
        step,

        // computed property
        isFormValid,
        isStep4Valid,


        // methods
        onAccountTypeChange,
        onStep2FormChange,
        onStep3FormChange,
        onStep4FormChange,
        submit,
        handleContinue,
        handleBack,
        handleFormBlur,
    };
}
